import * as blueprint from '@adc/blueprint'
import { match } from 'ts-pattern'
import type {
  AuctionUser,
  AuthenticatedAuctionUser,
} from '@adc/blueprint/lib/primitives'
import * as graph from '@adc/sdk'
import type { Maybe, PartyPhone } from '@adc/sdk/types'
import { PhoneVerification } from '@adc/ui-adc'
import _ from 'lodash'
import { getDefaultSaveSearchNameFromFilters } from '../../../converters/filters'

export interface SavedSearchModalProps {
  data: ReturnType<typeof blueprint.search.hooks.useListingsSearch>['data']
  user: AuctionUser
}

export const getSavedSearchLookup = (
  userSavedSearchData: AuthenticatedAuctionUser,
  apiFilters: SavedSearchModalProps['data']['listingCompatabilityFilters']
) => {
  const keysToSkipChecking = ['offset', 'limit', 'coords', 'geo_location']
  const savedSearchParameters = _.omit(apiFilters, keysToSkipChecking)
  ;(['nearby_search_radius', 'version'] as (keyof typeof apiFilters)[]).forEach(
    key => {
      if (savedSearchParameters[key]) {
        // @ts-ignore
        savedSearchParameters[key] = String(savedSearchParameters[key])
      }
    }
  )
  const apiValue = {
    ...savedSearchParameters,
    // TODO: lines below seem useless. Keeping for for backwards compat, because current saved search
    // actions adds them to the payload when saving search
    auction_type: 'residential',
    use_resi_collection: 'y',
    show_sold_assets: 'y',
  }
  const savedSearches = userSavedSearchData.saved_searches ?? []
  const savedSearch = _.find(savedSearches, o =>
    _.isEqual(
      _.omit(o?.parameters as typeof apiFilters, keysToSkipChecking),
      apiValue
    )
  )
  return savedSearch || null
}

export const getSaveSearchParametersFromBlueprintData = (
  data: SavedSearchModalProps['data']
) => {
  const { currentFilters, listingCompatabilityFilters } = data

  const defaultSavedSearchName = _.trim(
    getDefaultSaveSearchNameFromFilters(currentFilters) as string
  )

  const apiValue = {
    ...listingCompatabilityFilters,
    offset: 0,
    auction_type: 'residential',
    use_resi_collection: 'y',
    show_sold_assets: 'y',
  }

  return [defaultSavedSearchName, apiValue] as [
    string,
    typeof listingCompatabilityFilters
  ]
}

export const getSaveSearchButtonLabel = (
  isSaved: boolean,
  smallButtons: boolean
) => {
  let saveSearchButtonLabel = isSaved ? 'Saved Search' : 'Save Search'
  if (smallButtons) saveSearchButtonLabel = isSaved ? 'Saved' : 'Save'
  return saveSearchButtonLabel
}

export const getModalVersionForAnalytics = (user: AuctionUser) => {
  if (!user.logged_in) {
    return 'logged-out'
  }

  const latestPhone = PhoneVerification.utils.getLatestPhone(
    user.party?.phones
  ) as Maybe<PartyPhone>

  const communicationPreferenceSmsOptIn =
    user.communication_preference?.rules?.find(
      r =>
        r?.channel === graph.enums.CommunicationPreferenceChannel.Sms &&
        r?.audience === graph.enums.Audience.Buyer
    )

  const showLegalese =
    !latestPhone?.opt_in ||
    communicationPreferenceSmsOptIn?.opt_status !==
      graph.enums.OptStatus.OptedIn

  const hasPhoneNumber = !!latestPhone
  const isOptIn = !!latestPhone?.opt_in
  const isVerified = !!latestPhone?.verified

  const condition = {
    hasPhoneNumber,
    isOptIn,
    isVerified,
    showLegalese,
  }
  return match(condition)
    .with(
      {
        hasPhoneNumber: false,
        isOptIn: false,
        isVerified: false,
        showLegalese: true,
      },
      () => 'no-phone'
    )
    .with(
      {
        hasPhoneNumber: true,
        isOptIn: false,
        isVerified: false,
        showLegalese: true,
      },
      () => 'phone-not-verified'
    )
    .with(
      {
        hasPhoneNumber: true,
        isOptIn: false,
        isVerified: true,
        showLegalese: true,
      },
      () => 'opt-in-first-time'
    )
    .with(
      {
        hasPhoneNumber: true,
        isOptIn: false,
        isVerified: true,
        showLegalese: true,
      },
      () => 'opt-in-first-time'
    )
    .with(
      {
        hasPhoneNumber: true,
        isOptIn: true,
        isVerified: true,
        showLegalese: false,
      },
      () => 'opt-in-other-times'
    )
    .otherwise(() => null)
}
